import type { Expression } from 'mapbox-gl';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import type { ColorByAttribute, DatasetConfig } from './types';

/**
 * Reorders one group's rules within the flat rule list, leaving every other
 * group's rules where they are. A legend only shows a single group's slice, so
 * a drag there must not disturb how the groups interleave in the flat,
 * precedence-ordered list (later rules win — see buildColorByExpression).
 * @param {ColorByRule[]} rules The full flat rule list.
 * @param {string} groupId The group being reordered (its rules' groupId).
 * @param {ColorByRule[]} orderedGroupRules That group's rules in the new order.
 * @return {ColorByRule[]} A new flat list with the group reordered in place.
 */
export function reorderWithinGroup(
  rules: ColorByRule[],
  groupId: string,
  orderedGroupRules: ColorByRule[]
): ColorByRule[] {
  // Positional splice below is only correct for a full group slice; bail if the
  // caller passed a filtered subset, else the flat list gets undefined holes.
  if (
    orderedGroupRules.length !==
    rules.filter((r) => r.groupId === groupId).length
  ) {
    return rules;
  }
  let i = 0;
  return rules.map((rule) => {
    if (rule.groupId !== groupId) return rule;
    return orderedGroupRules[i++];
  });
}

// Vector-tile booleans don't reliably decode to a JS boolean through the
// pipeline (ES's _mvt endpoint / mapbox-gl have been observed to hand back
// 'true'/1 as well) — mirrors the tri-state check in document-hover-popup.tsx.
function booleanMatch(prop: Expression, value: boolean): Expression {
  const truthy = [
    ['==', prop, true],
    ['==', prop, 'true'],
    ['==', prop, 1],
  ];
  const falsy = [
    ['==', prop, false],
    ['==', prop, 'false'],
    ['==', prop, 0],
  ];
  return ['any', ...(value ? truthy : falsy)] as Expression;
}

// Sort selected values by descending `precedence` (default 0) so a feature
// matching several picks the highest-precedence one. The map (buildColorByExpression)
// and the card accent (accentColorForRow) must order identically or their colors
// drift on overlap, so both sort through here. Stable sort keeps selection order
// within a precedence tier.
const byPrecedenceDesc =
  (valueTileMap: Record<string, { precedence?: number }>) =>
  (a: string, b: string): number =>
    (valueTileMap[b]?.precedence ?? 0) - (valueTileMap[a]?.precedence ?? 0);

/**
 * The tile property a scalar rule compares, as a string when the attribute asks.
 * @param {ColorByAttribute} attribute The attribute the rule colors by.
 * @param {string} mvtProperty Its tile property.
 * @return {Expression} The property's value expression.
 */
const tileValue = (
  attribute: ColorByAttribute,
  mvtProperty: string
): Expression =>
  (attribute.matchAsString
    ? ['to-string', ['get', mvtProperty]]
    : ['get', mvtProperty]) as Expression;

/**
 * Builds a Mapbox color value from a dataset's enabled color rules. Every
 * enabled rule whose attribute maps to a tile property paints the features it
 * matches; a feature matching several rules takes the LAST rule's color (the
 * rules are laid out in reverse inside a `case`, whose first matching branch
 * wins). Features that match no rule — and rules on attributes with no
 * `mvtProperty`/`valueTileMap` entry (not on the vector tiles) — fall through
 * to the flat fallback (the section's default layer color).
 * @param {ColorByRule[]} rules The dataset's color rules.
 * @param {DatasetConfig} config The active dataset config.
 * @param {string} fallback The default color when no rule matches.
 * @return {string | Expression} A flat color or a Mapbox case expression.
 */
export function buildColorByExpression(
  rules: ColorByRule[],
  config: DatasetConfig,
  fallback: string
): string | Expression {
  const attributes = config.colorByAttributes ?? [];
  // Only rules the user is painting on the Map (applyTo.map) recolor tiles.
  const enabled = rules.filter(
    (r) => r.enabled && r.selectedValues.length > 0 && r.applyTo?.map !== false
  );

  // Later rules win, so emit them first: `case` returns the first matching
  // branch, and reversing makes the last rule the first branch.
  const branches: Expression[] = [];
  for (let i = enabled.length - 1; i >= 0; i -= 1) {
    const rule = enabled[i];
    const attribute = attributes.find((a) => a.facetKey === rule.attribute);
    if (!attribute) continue;

    if (attribute.valueTileMap) {
      const { valueTileMap } = attribute;
      // Each selected value may live on its own scalar tile property with its
      // own value domain; values with no entry have no tile representation
      // (cards only) and are skipped here. Higher-precedence values sort first
      // so a feature matching several picks the intended one (case = first win).
      const mapped = rule.selectedValues
        .filter((v) => valueTileMap[v])
        .sort(byPrecedenceDesc(valueTileMap));
      if (!mapped.length) continue;

      const conditionFor = (value: string): Expression => {
        const { mvtProperty, mvtValue } = valueTileMap[value];
        const prop = ['get', mvtProperty] as Expression;
        return typeof mvtValue === 'boolean'
          ? booleanMatch(prop, mvtValue)
          : (['==', prop, mvtValue] as Expression);
      };

      const enterCondition = ['any', ...mapped.map(conditionFor)] as Expression;
      const colorExpr = [
        'case',
        ...mapped.flatMap((v) => [
          conditionFor(v),
          rule.valueColors[v] ?? fallback,
        ]),
        fallback,
      ] as Expression;
      branches.push(enterCondition, colorExpr);
      continue;
    }

    if (!attribute.mvtProperty) continue;

    if (attribute.multiValued) {
      // The tile property is a delimiter-wrapped set like "|A|B|"; wrap each
      // selected value in pipes and match by containment. `to-string` keeps
      // missing/null features safe, and the first matching value's color wins.
      const prop = ['to-string', ['get', attribute.mvtProperty]] as Expression;
      const enterCondition = [
        'any',
        ...rule.selectedValues.map((v) => ['in', `|${v}|`, prop]),
      ] as Expression;
      const colorExpr = [
        'case',
        ...rule.selectedValues.flatMap((v) => [
          ['in', `|${v}|`, prop],
          rule.valueColors[v] ?? fallback,
        ]),
        fallback,
      ] as Expression;
      branches.push(enterCondition, colorExpr);
      continue;
    }

    const pairs = rule.selectedValues.flatMap((value) => [
      value,
      rule.valueColors[value] ?? fallback,
    ]);
    if (!pairs.length) continue;

    const prop = tileValue(attribute, attribute.mvtProperty);
    // Enter this rule only when the feature's value is one it selects; the
    // inner `match` then resolves the per-value color.
    branches.push(
      ['in', prop, ['literal', rule.selectedValues]] as Expression,
      ['match', prop, ...pairs, fallback] as Expression
    );
  }

  if (!branches.length) return fallback;
  return ['case', ...branches, fallback] as Expression;
}

/**
 * The card left-accent color for a row: the last enabled table-applied rule
 * whose attribute this row carries a colored value for. Mirrors the map
 * (buildColorByExpression) — later rules win, and within a rule the map's own
 * ordering breaks overlaps (valueTileMap attributes by `precedence`, all others
 * by selection order) — so a card's stripe matches its map feature.
 * @param {Record<string, unknown>} row The card's row data.
 * @param {ColorByRule[]} rules The dataset's color rules.
 * @param {ColorByAttribute[]} attributes The dataset's color-by attributes.
 * @return {string | undefined} The accent color, or undefined when nothing matches.
 */
export function accentColorForRow(
  row: Record<string, unknown>,
  rules: ColorByRule[],
  attributes: ColorByAttribute[]
): string | undefined {
  for (let i = rules.length - 1; i >= 0; i -= 1) {
    const rule = rules[i];
    if (!rule.enabled || rule.selectedValues.length === 0) continue;
    // Cards are the "Table" surface; skip rules not painting the table.
    if (rule.applyTo?.table === false) continue;
    const attr = attributes.find((a) => a.facetKey === rule.attribute);
    if (!attr?.rowField) continue;
    const value = row[attr.rowField];
    if (value === null || value === undefined) continue;
    // Walk the SELECTED values in the same order the map paints them
    // (buildColorByExpression): valueTileMap attributes sort by precedence, all
    // others keep selection order. The first selected value this row carries and
    // the rule colors wins — matching the map's first-branch-wins `case`, so a
    // multi-valued row (e.g. two selected operators) accents the same color the
    // map fills.
    // Compared uppercased on both sides: a token can differ in case between surfaces.
    // Wells `status` is indexed uppercase, so the tiles and the facets (and so a rule's
    // values) carry PERMIT, while the table row carries the stored `permit`.
    const rowValues = new Set(
      (Array.isArray(value) ? value : [value]).map((v) =>
        String(v).toUpperCase()
      )
    );
    const ordered = attr.valueTileMap
      ? rule.selectedValues.slice().sort(byPrecedenceDesc(attr.valueTileMap))
      : rule.selectedValues;
    // The `&& valueColors[v]` guard skips a colorless winner for the next colored
    // value, so such a card shows no stripe where the map paints its fallback.
    // Only reachable with a selected-but-colorless value, which the modal prevents.
    const match = ordered.find(
      (v) => rowValues.has(String(v).toUpperCase()) && rule.valueColors[v]
    );
    if (match) return rule.valueColors[match];
  }
  return undefined;
}
